// NEW_FILE_JUSTIFIED: usePreAuthDetailActions — manages all dialog open state, status transitions, and internal notes saving for PreAuthDetailPage; extracted from component because 6 useState calls exceeded the max-2-useState-per-component rule.
import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ require two levels up to reach convex/_generated/
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import type { TPreAuthStatus } from '@/@types/claims';

interface IUsePreAuthDetailActionsArgs {
  // LOCAL_TYPE_JUSTIFIED: local args interface for this hook file, not a shared domain type
  preAuthId: Id<'preAuthorizations'>;
  currentStatus: TPreAuthStatus;
  internalNotes: string | undefined;
}

export function usePreAuthDetailActions({
  preAuthId,
  currentStatus,
  internalNotes,
}: IUsePreAuthDetailActionsArgs) {
  const updateStatus = useMutation(api.insurance.preAuthorizations.updateStatus);

  const [approveOpen, setApproveOpen] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [notes, setNotes] = useState(internalNotes ?? '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  async function handleStatusChange(newStatus: string) {
    if (!newStatus) return;
    if (newStatus === 'approved') {
      setApproveOpen(true);
      return;
    }
    if (newStatus === 'denied') {
      setDenyOpen(true);
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await updateStatus({
        preAuthId,
        status: newStatus as TPreAuthStatus, // AS_JUSTIFIED: newStatus comes from STATUS_TRANSITIONS[currentStatus] — a Record<TPreAuthStatus, TPreAuthStatus[]>, guaranteed to be a valid TPreAuthStatus value
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleSaveNotes() {
    setIsSavingNotes(true);
    try {
      await updateStatus({
        preAuthId,
        status: currentStatus,
        internalNotes: notes,
      });
    } finally {
      setIsSavingNotes(false);
    }
  }

  return {
    approveOpen,
    setApproveOpen,
    denyOpen,
    setDenyOpen,
    linkOpen,
    setLinkOpen,
    notes,
    setNotes,
    isSavingNotes,
    isUpdatingStatus,
    handleStatusChange,
    handleSaveNotes,
  };
}
